import { forwardRef, useCallback, useEffect, useState } from 'react';
import { adminApi, formatCurrency } from '../../utils/api';
import Icon from '../../components/Icon';
import { LoadState, openInApp } from './parts';

// כפילויות מספרי מסמך — the last card on מבט על, collapsed by default (owner,
// 27/09/2026; it used to be the בריאות page, which is gone).
//
// Until this list is empty the DB-level uniqueness index cannot be created —
// the migration scans at every boot and skips the index while duplicates exist.
// So this is not tidiness: the number gates a real protection.
//
// Collapsed, the header still answers the one question worth seeing without a
// click — is the protection on? — from the overview payload the page already
// has (`data_health`), so a closed card costs no request. The groups themselves
// load when the card is opened, and again on every re-open, so what you read
// is never older than the click that showed it.

function StatusPill({ indexActive, groupCount }) {
  if (indexActive) return <span className="cc-pill ok">מוגן</span>;
  if (!groupCount) return <span className="cc-pill warn">ההגנה לא פעילה</span>;
  return <span className="cc-pill bad">{groupCount} קבוצות לניקוי</span>;
}

function statusCopy(indexActive, groupCount) {
  if (indexActive) return 'הגנת הייחודיות פעילה — שמירת מסמך עם מספר שכבר קיים תיחסם אוטומטית.';
  if (!groupCount) return 'אין כרגע כפילויות, אבל ההגנה עוד לא הופעלה — הפעלה מחדש של השרת תפעיל אותה.';
  return `${groupCount} קבוצות כפילויות. יש לפתור (מחיקה או מיזוג) לפני שההגנה תופעל, ואז להפעיל מחדש את השרת.`;
}

function GroupsTable({ groups }) {
  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>מסעדה</th>
            <th>ספק</th>
            <th>מספר מסמך</th>
            <th>מסמכים מתנגשים</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g, gi) => (
            <tr key={`${g.restaurant_id}-${g.supplier_id}-${g.doc_num}-${gi}`}>
              <td>{g.docs[0]?.restaurant_name || '—'}</td>
              <td style={{ fontWeight: 600 }}>{g.docs[0]?.supplier_name || `ספק ${g.supplier_id}`}</td>
              <td style={{ color: '#dc2626', fontWeight: 700 }}>{g.doc_num}</td>
              <td>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {g.docs.map(d => (
                    <div key={d.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="badge badge-blue">{d.doc_type}</span>
                      <span className="cc-muted" style={{ fontSize: '0.83rem' }}>{d.date || '—'}</span>
                      <span style={{ fontSize: '0.83rem', fontWeight: 600 }}>{formatCurrency(d.total)}</span>
                      <span className="cc-muted" style={{ fontSize: '0.78rem' }}>סטטוס: {d.status}</span>
                      {/* CROSS-TENANT list, so the document carries its own
                          restaurant — see parts.jsx#openInApp. */}
                      <button className="btn btn-sm btn-outline" onClick={() => openInApp(`/document/${d.id}`, g.restaurant_id)}>
                        <Icon name="external" size={13} /> מסמך #{d.id}
                      </button>
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useDuplicateGroups(open) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    setData(null);
    try {
      const r = await adminApi.getDuplicateDocs();
      setData(r.data);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);
  return { data, error, load };
}

/**
 * @param open, onToggle  controlled by the page, so its status tile can open it
 * @param indexActive, groupCount  from the overview's `data_health`, for the
 *        collapsed header; superseded by the loaded list once it arrives
 */
const DuplicateDocsPanel = forwardRef(function DuplicateDocsPanel(
  { open, onToggle, indexActive, groupCount }, ref,
) {
  const { data, error, load } = useDuplicateGroups(open);
  const active = data ? data.index_active : indexActive;
  const count = data ? (data.groups || []).length : groupCount;

  return (
    <div className="cc-card cc-collapse" ref={ref}>
      <button
        type="button"
        className="cc-collapse-head"
        aria-expanded={open}
        aria-controls="cc-duplicates-body"
        onClick={onToggle}
      >
        <h2 className="cc-card-title">כפילויות מספרי מסמך</h2>
        <span className="cc-muted" style={{ fontSize: '0.79rem' }}>מסמכים מאושרים בלבד</span>
        <span className="cc-spacer" />
        <StatusPill indexActive={active} groupCount={count} />
        <Icon name="chevron" size={16} className={`cc-collapse-caret${open ? ' is-open' : ''}`} />
      </button>

      {open && (
        <div id="cc-duplicates-body" className="cc-collapse-body">
          <LoadState loading={!data && !error} error={error} onRetry={load} empty={false}>
            {data && (
              <>
                <p className="cc-health-copy">{statusCopy(active, count)}</p>
                {count === 0
                  ? <div className="cc-empty">אין כפילויות לפתרון</div>
                  : <GroupsTable groups={data.groups} />}
              </>
            )}
          </LoadState>
        </div>
      )}
    </div>
  );
});

export default DuplicateDocsPanel;
